/**
 * Confidence Breakdown - Detailed explanation of how a mention's confidence score was calculated
 *
 * Matches backend DTO from:
 * Backend/MentionedMe.Application/Features/RedditMentions/DTOs/MentionReviewDto.cs
 *
 * SESSION_043: Added for transparency and algorithm improvement
 */

export interface ConfidenceBreakdownDto {
  matchType: string;
  baseScore: number;
  nerBoost: number;
  contextBoost: number;
  penalties: ConfidencePenaltyDto[];
  finalScore: number;
  explanation: string;
  metadata: Record<string, string>;
  confidenceLevel: string;
}

export interface ConfidencePenaltyDto {
  type: string;
  amount: number;
  reason: string;
}

/**
 * Match type categories
 */
export const MATCH_TYPES = {
  EXACT_MATCH: 'ExactMatch',
  ALIAS: 'Alias',
  NAME: 'Name',
  PARTIAL_ADDRESS: 'PartialAddress',
  FUZZY: 'Fuzzy',
  WEAK: 'Weak'
} as const;

/**
 * Match type display labels
 */
export const MATCH_TYPE_LABELS: Record<string, string> = {
  [MATCH_TYPES.EXACT_MATCH]: 'Exact Match (URL/Phone/Address/Name)',
  [MATCH_TYPES.ALIAS]: 'Alias Match',
  [MATCH_TYPES.NAME]: 'Name Match',
  [MATCH_TYPES.PARTIAL_ADDRESS]: 'Partial Address',
  [MATCH_TYPES.FUZZY]: 'Fuzzy Match',
  [MATCH_TYPES.WEAK]: 'Weak Match'
};

/**
 * Match type colors for badges (Mantine colors)
 */
export const MATCH_TYPE_COLORS: Record<string, string> = {
  [MATCH_TYPES.EXACT_MATCH]: 'green',
  [MATCH_TYPES.ALIAS]: 'teal',
  [MATCH_TYPES.NAME]: 'blue',
  [MATCH_TYPES.PARTIAL_ADDRESS]: 'cyan',
  [MATCH_TYPES.FUZZY]: 'yellow',
  [MATCH_TYPES.WEAK]: 'red'
};

/**
 * Get display label for match type
 */
export function getMatchTypeLabel(matchType: string): string {
  return MATCH_TYPE_LABELS[matchType] || matchType;
}

/**
 * Get badge color for match type
 */
export function getMatchTypeColor(matchType: string): string {
  return MATCH_TYPE_COLORS[matchType] || 'gray';
}
